import React, { useState, useEffect } from 'react'
import { BotType } from './chatWidget/types'
import { checkWidgetVisibility } from './chatWidget/widgetVisibility/checkWidgetVisibility'
import { checkPercentageVisibilityForCategory } from './chatWidget/widgetVisibility/checkPercentageVisibilityForCategory'
import { getPercentageVisibilityByTag } from './chatWidget/widgetVisibility/getPercentageVisibilityByTag'
import { getPageStatus } from './chatWidget/customerServiceIntegration/getPageStatus'
import { logDevelopmentMessage } from './chatWidget/utils/logDevelopmentMessage'
import { loadMIAW } from './chatWidget/loadMIAW'
import { isPreviousChatSession } from './chatWidget/utils/handlePreviousSession'
import { getBotType, getLoadType } from './chatWidget/utils'
import JoyfullyBoldTheme from './themes/JoyfullyBoldTheme'
import RedesignTheme from './themes/RedesignTheme'
import useStorageListener from './hooks/useStorageListener'
import useUrlChange from './hooks/useUrlChange'
import { FEATURE_SHOW_MIAW_CHAT, FEATURE_ENABLE_AF_CHAT } from '../consts/env'
import { pollForWidgetFlagAndShowAppropriateWidget } from './chatWidget/utils/pollForWidgetFlag'
import adjustGoToTopPosition from './chatWidget/utils/adjustGoToTopPosition'
import { getIsDebugTestingActive } from './chatWidget/utils/general'

const ChatWidget = () => {
  logDevelopmentMessage('=== Chatwidget component ===')
  const [pageStatus, setPageStatus] = useState(() => getPageStatus())
  logDevelopmentMessage(`pageStatus: ${JSON.stringify(pageStatus)}`)
  const [botTypeValue, setBotTypeValue] = useState(getBotType())

  const isMIAWSession = isPreviousChatSession()

  useStorageListener(() => setBotTypeValue(getBotType()))

  useUrlChange(() => {
    logDevelopmentMessage('setting page status useUrlChange')
    setPageStatus(getPageStatus())
  })

  const loadAppropriateService = React.useCallback(async () => {
    const shouldShowMiawChat = await shouldLoadMIAWChat()

    const loadType = getLoadType({
      isAgentForcePage: pageStatus.isAgentForceServicablePage,
      isDefaultMIAWPage: FEATURE_SHOW_MIAW_CHAT ? shouldShowMiawChat : pageStatus.isDefaultMIAWServicablePage,
    })

    logDevelopmentMessage(
      `Loading service for chatBotType: ${loadType}, isInACustomerServicePage: ${pageStatus.isInCustomerServicePage}, isAgentForcePage: ${pageStatus.isAgentForceServicablePage}, isDefaultMIAWPage: ${pageStatus.isDefaultMIAWServicablePage}`
    )

    if (loadType === BotType.DEFAULT || (loadType === BotType.AGENT_FORCE && FEATURE_ENABLE_AF_CHAT)) {
      loadMIAW(loadType)
    } else {
      // in case we dont have MIAW chat on page, we still need to check and may load CW widget
      pollForWidgetFlagAndShowAppropriateWidget()
    }
  }, [pageStatus])

  const shouldLoadMIAWChat = async () => {
    if (getIsDebugTestingActive()) {
      logDevelopmentMessage('Debug testing active - forcing MIAW chat to load')
      return true
    }
    
    const { shouldLoadWidget, isInitializingFromPreviousSession, categoryApplicableForQueue } =
      await checkWidgetVisibility()
    const shouldAbortWidgetLoading = !isInitializingFromPreviousSession && !shouldLoadWidget
    if (shouldAbortWidgetLoading && !pageStatus.isInCustomerServicePage) {
      logDevelopmentMessage(`returning as chat session is active`)
      return false
    }

    if (window.hideSalesLiveChat) {
      return false
    }

    let isWithinTargetPercentage = false

    if (!pageStatus.isInCustomerServicePage) {
      try {
        const { percentageVisibility, withinOpenHours } = await getPercentageVisibilityByTag(categoryApplicableForQueue)
        isWithinTargetPercentage = checkPercentageVisibilityForCategory({
          categoryApplicableForQueue,
          withinOpenHours,
          targetPercentage: percentageVisibility,
        })
      } catch (err: any) {
        logDevelopmentMessage(`Error fetching percentage visibility: ${err.message}`)
      }

      if (!isWithinTargetPercentage && !isInitializingFromPreviousSession) {
        logDevelopmentMessage('Skipping loadESW due to target percentage conditions.')
        return false
      } else {
        return shouldLoadWidget && isWithinTargetPercentage
      }
    } else {
      return true
    }
  }

  useEffect(() => {
    adjustGoToTopPosition()
  }, [])

  useEffect(() => {
    loadAppropriateService()
  }, [loadAppropriateService, isMIAWSession, botTypeValue])

  return pageStatus.isInCustomerServicePage && !FEATURE_SHOW_MIAW_CHAT ? <JoyfullyBoldTheme /> : <RedesignTheme />
}

export default ChatWidget
